import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Minimize2, Layers, Image as ImageIcon, ScanText, Scissors, RotateCw, ImageDown, Stamp, EyeOff, FileText,
  Lock, LockOpen, Eraser, FilePen, Receipt, ArrowUpRight, Check,
} from "lucide-react";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { WingmanChat } from "@/components/WingmanChat";
import { ENGINE_TO_SLUG } from "@/data/tools";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];

const ENGINE_ICONS = {
  compress: Minimize2, merge: Layers, img2pdf: ImageIcon, extract: ScanText, pages: Scissors,
  rotate: RotateCw, pdf2img: ImageDown, pdf2docx: FileText, watermark: Stamp, redact: EyeOff,
  protect: Lock, unlock: LockOpen, scrub: Eraser, edit: FilePen, invoice: Receipt,
};

const FALLBACK_ACCESS = [
  { plan: "Independent", price: "$5/month", access: "All 16 engines · 20 conversions per month · 15MB per file" },
  { plan: "Professional", price: "$50/month", access: "All 16 engines · unlimited conversions · 50MB per file · batch-ready · 14-day free trial" },
  { plan: "Enterprise", price: "Custom", access: "Firm-wide access · custom integrations · governance controls" },
];

export default function Engines() {
  const [engines, setEngines] = useState([]);
  const [access, setAccess] = useState(FALLBACK_ACCESS);

  useEffect(() => {
    axios.get(`${API}/engines`).then((res) => {
      setEngines(res.data.engines ?? []);
      setAccess(res.data.access ?? FALLBACK_ACCESS);
    }).catch(() => {});
  }, []);

  return (
    <main className="bg-page text-ink min-h-screen" data-testid="engines-page">
      <Nav />

      <section className="pt-32 md:pt-40 pb-16 md:pb-20 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.2, ease: EASE }}>
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-mist block mb-5">The engine room</span>
            <h1 className="font-medium tracking-tight leading-tight text-4xl md:text-5xl lg:text-6xl max-w-3xl mb-6" data-testid="engines-title">
              Sixteen engines. One precision workspace.
            </h1>
            <p className="text-base text-fog leading-relaxed max-w-2xl mb-8">
              Every engine runs in your workspace with zero document retention. Pick a plan, drop a file, and the
              output downloads in seconds — nothing is stored, ever.
            </p>
            <a href="/#pricing" data-testid="engines-cta"
              className="group inline-flex items-center gap-1.5 v4-btn-primary text-sm font-semibold h-11 px-6 rounded-full transition-colors duration-300">
              See access plans
              <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
            </a>
          </motion.div>
        </div>
      </section>

      <section className="pb-20 md:pb-28 px-6 md:px-12">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6" data-testid="engines-grid">
          {engines.map((e, i) => {
            const Icon = ENGINE_ICONS[e.id] || FileText;
            return (
              <motion.div
                key={e.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.7, ease: EASE, delay: (i % 3) * 0.08 }}
                className="bg-white border border-hairline rounded-xl p-7 hover:-translate-y-1 transition-transform duration-500"
                data-testid={`engine-card-${e.id}`}
              >
                <Icon className="w-7 h-7 text-ink mb-6" strokeWidth={1.5} />
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist block mb-2">
                  {String(i + 1).padStart(2, "0")} / {e.tagline}
                </span>
                <h2 className="text-lg font-medium tracking-tight mb-2">{e.name}</h2>
                <p className="text-sm text-fog leading-relaxed mb-4">{e.description}</p>
                <p className="text-xs text-fog leading-relaxed mb-4">
                  <span className="text-ink font-medium">Best for:</span> {e.best_for}
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-mist border border-hairline rounded-full px-2.5 py-1">{e.output}</span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-azure border border-azure/30 rounded-full px-2.5 py-1">{e.limits}</span>
                </div>
                {ENGINE_TO_SLUG[e.id] && (
                  <Link
                    to={`/${ENGINE_TO_SLUG[e.id]}`}
                    data-testid={`engine-open-${e.id}`}
                    className="group/link inline-flex items-center gap-1.5 text-sm font-medium text-azure mt-5 transition-colors duration-300"
                  >
                    Open the free tool
                    <ArrowUpRight className="w-4 h-4 group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5 transition-transform duration-300" />
                  </Link>
                )}
              </motion.div>
            );
          })}
          {engines.length === 0 && (
            <p className="text-sm text-fog col-span-full" data-testid="engines-loading">Loading the engine catalog…</p>
          )}
        </div>
      </section>

      <section className="pb-24 md:pb-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1, ease: EASE }}
          >
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-mist block mb-5">Access points</span>
            <h2 className="font-medium tracking-tight text-3xl md:text-4xl mb-10">Where every plan reaches.</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6" data-testid="access-matrix">
            {access.map((a, i) => (
              <motion.div
                key={a.plan}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.7, ease: EASE, delay: i * 0.08 }}
                className="bg-white border border-hairline rounded-xl p-7"
                data-testid={`access-${a.plan.toLowerCase()}`}
              >
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist block mb-3">{a.plan}</span>
                <p className="text-xl font-medium tracking-tight mb-4">{a.price}</p>
                <div className="space-y-2">
                  {a.access.split("·").map((part) => (
                    <p key={part} className="flex items-start gap-2 text-sm text-fog leading-relaxed">
                      <Check className="w-3.5 h-3.5 text-azure shrink-0 mt-0.5" strokeWidth={2} /> {part.trim()}
                    </p>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
      <WingmanChat />
    </main>
  );
}
